import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Sequence,
  Audio,
  Img,
  staticFile,
  Easing,
} from "remotion";
import {SPRINGS, FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK, IDV} from "../presets/idv";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {GradientText} from "../components/dimus/GradientText";
import {SceneTransition} from "../components/dimus/SceneTransition";
import {Counter} from "../components/dimus/Counter";
import {OrganicLayer} from "../components/overlays/OrganicLayer";

/**
 * CAFÉ COM TEU ROI EP10 — "Tempo até 1º Pagamento"
 * Trilogia completa: EP08 (CAC) → EP09 (LTV/CAC) → EP10 (Tempo)
 * BoardCopy: Halbert (voz pessoal) + Hormozi (Time Delay) + Sugarman (harmonização)
 * Editing Style: DYNAMIC (Dark Premium)
 */

const YELLOW = IDV.third;
const GREEN = IDV.secondary;
const RED = IDV.error;
const BLUE = IDV.primary;
const WHITE = "#f5f5f7";
const MUTED = "#a3a3a3";

const SZ = SAFE_ZONES.universal;

const SCENES = [
  {dur: 162, file: "audio/cafe-ep10/cafe-ep10_c01.mp3", dir: "right" as const},
  {dur: 301, file: "audio/cafe-ep10/cafe-ep10_c02.mp3", dir: "left" as const},
  {dur: 1126, file: "audio/cafe-ep10/cafe-ep10_c03.mp3", dir: "right" as const},
  {dur: 490, file: "audio/cafe-ep10/cafe-ep10_c04.mp3", dir: "up" as const},
  {dur: 422, file: "audio/cafe-ep10/cafe-ep10_c05.mp3", dir: "down" as const},
];

export const CAFE_EP10_TOTAL = SCENES.reduce((s, sc) => s + sc.dur, 0);

// ─── Pill badge ──────────────────────────────────────────────────────
const Pill: React.FC<{
  text: string; color: string; delay: number; glow?: boolean; icon?: string;
}> = ({text, color, delay, glow, icon}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.snappy});
  const pulse = glow ? 1 + Math.sin(frame * 0.08) * 0.03 : 1;

  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p,
      transform: `scale(${interpolate(p, [0, 1], [0.7, 1], CLAMP) * pulse})`,
      padding: "10px 20px", borderRadius: 999, display: "flex", alignItems: "center", gap: 8,
      background: `${color}18`, border: `1px solid ${color}44`,
    }}>
      {icon && <span style={{fontSize: 20}}>{icon}</span>}
      <span style={{fontSize: FONT_SCALE.label.min, fontWeight: 700, color, fontFamily: FONT_STACK}}>
        {text}
      </span>
    </div>
  );
};

// ─── Cashflow Bar (red going down) ───────────────────────────────────
const CashBar: React.FC<{
  month: string; amount: string; maxH: number; delay: number;
}> = ({month, amount, maxH, delay}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.smooth});
  const h = interpolate(p, [0, 1], [0, maxH], CLAMP);
  const glow = Math.sin(frame * 0.06) * 0.15 + 0.85;

  return (
    <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: 8, flex: 1}}>
      <div style={{
        width: 80, height: h, borderRadius: 12,
        background: `linear-gradient(180deg, ${RED}cc, ${RED}66)`,
        opacity: glow, boxShadow: `0 0 20px ${RED}33`,
      }} />
      <div style={{fontSize: 22, fontWeight: 800, color: RED, fontFamily: FONT_STACK, opacity: p}}>
        {amount}
      </div>
      <div style={{fontSize: 20, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK, opacity: p}}>
        {month}
      </div>
    </div>
  );
};

// ─── Client Row (for Scene 4) ────────────────────────────────────────
const ClientRow: React.FC<{
  num: number; contact: string; payment: string; days: number; delay: number;
}> = ({num, contact, payment, days, delay}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.snappy});
  const dayColor = days > 60 ? RED : days > 45 ? YELLOW : GREEN;

  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p,
      transform: `translateX(${interpolate(p, [0, 1], [40, 0], CLAMP)}px)`,
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "14px 20px", borderRadius: 14, width: "100%",
      background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)",
    }}>
      <div style={{fontSize: 24, fontWeight: 600, color: MUTED, fontFamily: FONT_STACK, width: 120}}>
        Cliente {num}
      </div>
      <div style={{fontSize: 22, fontWeight: 500, color: WHITE, fontFamily: FONT_STACK, flex: 1, textAlign: "center"}}>
        {contact} → {payment}
      </div>
      <div style={{
        fontSize: 28, fontWeight: 800, color: dayColor, fontFamily: FONT_STACK,
        padding: "4px 14px", borderRadius: 999,
        background: `${dayColor}15`, border: `1px solid ${dayColor}33`,
      }}>
        {days}d
      </div>
    </div>
  );
};

// ─── Trilogy Badge (for Scene 5) ─────────────────────────────────────
const TrilogyBadge: React.FC<{
  num: string; label: string; sub: string; color: string; delay: number; highlight?: boolean;
}> = ({num, label, sub, color, delay, highlight}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.bouncy});
  const glow = highlight ? 1 + Math.sin(frame * 0.06) * 0.04 : 1;

  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p,
      transform: `translateY(${interpolate(p, [0, 1], [40, 0], CLAMP)}px) scale(${interpolate(p, [0, 1], [0.8, highlight ? 1.05 : 1], CLAMP) * glow})`,
      width: 260, padding: "24px 16px", borderRadius: 20,
      background: `${color}12`, border: `2px solid ${color}${highlight ? "88" : "33"}`,
      display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
      boxShadow: highlight ? `0 0 30px ${color}22` : "none",
    }}>
      <div style={{
        fontSize: 36, fontWeight: 900, color, fontFamily: FONT_STACK,
        width: 52, height: 52, borderRadius: "50%",
        background: `${color}20`, display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {num}
      </div>
      <div style={{fontSize: 28, fontWeight: 800, color: WHITE, fontFamily: FONT_STACK}}>
        {label}
      </div>
      <div style={{fontSize: 20, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK, textAlign: "center"}}>
        {sub}
      </div>
    </div>
  );
};

// ─── SCENE 1: ABERTURA ───────────────────────────────────────────────
const S1: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const logoP = spring({frame, fps, config: SPRINGS.bouncy});
  const titleP = spring({frame: frame - 15, fps, config: SPRINGS.heavy});

  return (
    <OrganicLayer intensity="subtle" seed="cafe-ep10">
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 24}}>
      <Audio src={staticFile(SCENES[0].file)} volume={0.9} />
      <Audio src={staticFile("sfx/impact/impact-02.mp3")} volume={0.5} />
      <Sequence from={10} durationInFrames={10}>
        <Audio src={staticFile("sfx/pop/pop-01.mp3")} volume={0.4} />
      </Sequence>

      {/* Logo */}
      <div style={{
        opacity: logoP,
        transform: `scale(${interpolate(logoP, [0, 1], [0.3, 1], CLAMP)})`,
      }}>
        <Img src={staticFile("assets/profiles/logo-cafe-roi-dark-bg.png")} style={{height: 120, objectFit: "contain"}} />
      </div>

      {/* EP badge */}
      <Pill text="EPISODIO 10" color={YELLOW} delay={8} icon="⏱" />

      {/* Title */}
      <div style={{
        opacity: titleP,
        transform: `scale(${interpolate(titleP, [0, 1], [0.9, 1], CLAMP)})`,
        textAlign: "center",
      }}>
        <div style={{fontSize: FONT_SCALE.h1.min, fontWeight: 800, color: WHITE, fontFamily: FONT_STACK, lineHeight: 1.15}}>
          O NUMERO QUE QUEBRA
        </div>
        <GradientText text="MAIS EMPRESA" colors={[YELLOW, "#FFE066"]} fontSize={FONT_SCALE.h1.min} delay={20} springProfile="heavy" />
      </div>
    </AbsoluteFill>
    </OrganicLayer>
  );
};

// ─── SCENE 2: PRINCÍPIO ─────────────────────────────────────────────
const S2: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const prinP = spring({frame: frame - 5, fps, config: SPRINGS.smooth});
  const titleP = spring({frame: frame - 20, fps, config: SPRINGS.heavy});
  const subP = spring({frame: frame - 80, fps, config: SPRINGS.smooth});
  const pillsP = spring({frame: frame - 150, fps, config: SPRINGS.snappy});

  return (
    <AbsoluteFill style={{
      justifyContent: "center", alignItems: "center",
      padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`,
      flexDirection: "column", gap: 28,
      background: `linear-gradient(170deg, ${BLUE} 0%, #0d1f3a 100%)`,
    }}>
      <Audio src={staticFile(SCENES[1].file)} volume={0.9} />
      <Sequence from={20} durationInFrames={10}>
        <Audio src={staticFile("sfx/impact/impact-03.mp3")} volume={0.5} />
      </Sequence>
      <Sequence from={150} durationInFrames={10}>
        <Audio src={staticFile("sfx/pop/pop-02.mp3")} volume={0.35} />
      </Sequence>

      {/* Princípio pill */}
      <div style={{
        opacity: prinP, padding: "10px 28px", borderRadius: 999,
        background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)",
        fontSize: FONT_SCALE.label.min, fontWeight: 600, color: GREEN, fontFamily: FONT_STACK,
        letterSpacing: 3, textTransform: "uppercase" as const,
      }}>
        PRINCIPIO DE HOJE
      </div>

      {/* Main text */}
      <div style={{textAlign: "center"}}>
        <div style={{
          opacity: titleP, fontSize: FONT_SCALE.h2.min, fontWeight: 700,
          color: WHITE, fontFamily: FONT_STACK, lineHeight: 1.2,
        }}>
          Nao e o custo que{"\n"}quebra empresa.
        </div>
        <div style={{
          opacity: titleP,
          transform: `scale(${interpolate(titleP, [0, 1], [0.85, 1], CLAMP)})`,
          fontSize: FONT_SCALE.h1.min, fontWeight: 900,
          color: YELLOW, fontFamily: FONT_STACK, marginTop: 8,
        }}>
          E O TEMPO.
        </div>
      </div>

      {/* Context sub */}
      <div style={{
        opacity: subP, fontSize: FONT_SCALE.body.min, fontWeight: 500,
        color: MUTED, fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.3,
      }}>
        Dois numeros entregues.{"\n"}Hoje eu fecho com o terceiro.
      </div>

      {/* Trilogy pills */}
      <div style={{
        opacity: pillsP, display: "flex", gap: 12,
        transform: `translateY(${interpolate(pillsP, [0, 1], [20, 0], CLAMP)}px)`,
      }}>
        <Pill text="✓ CAC" color={GREEN} delay={155} />
        <Pill text="✓ LTV/CAC" color={GREEN} delay={160} />
        <Pill text="③ TEMPO" color={YELLOW} delay={165} glow />
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 3: REFLEXÃO (longa — 3 fases internas) ───────────────────
const S3: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  // Phase boundaries (relative to scene start)
  const PHASE_B = 350;  // ~11.7s — switch to "ilusão"
  const PHASE_C = 750;  // ~25s — switch to "metáfora"

  // Phase 3A — O Problema (timeline de caixa)
  const cardP = spring({frame: frame - 5, fps, config: SPRINGS.smooth});
  const showPhaseA = frame < PHASE_B + 30;
  const fadeA = frame > PHASE_B ? interpolate(frame, [PHASE_B, PHASE_B + 20], [1, 0], CLAMP) : 1;

  // Phase 3B — A Ilusão
  const showPhaseB = frame >= PHASE_B - 10 && frame < PHASE_C + 30;
  const fadeB_in = interpolate(frame, [PHASE_B, PHASE_B + 15], [0, 1], CLAMP);
  const fadeB_out = frame > PHASE_C ? interpolate(frame, [PHASE_C, PHASE_C + 20], [1, 0], CLAMP) : 1;
  const fadeB = Math.min(fadeB_in, fadeB_out);

  // Phase 3C — A Metáfora
  const showPhaseC = frame >= PHASE_C - 10;
  const fadeC = interpolate(frame, [PHASE_C, PHASE_C + 15], [0, 1], CLAMP);

  // Mini-card status (for Phase B)
  const StatusMini: React.FC<{label: string; ok: boolean; delay: number}> = ({label, ok, delay: d}) => {
    const p2 = spring({frame: frame - PHASE_B - d, fps, config: SPRINGS.snappy});
    const color = ok ? GREEN : RED;
    const pulse2 = !ok ? 1 + Math.sin(frame * 0.08) * 0.03 : 1;
    return (
      <div style={{
        opacity: p2, transform: `scale(${interpolate(p2, [0, 1], [0.7, 1], CLAMP) * pulse2})`,
        padding: "14px 16px", borderRadius: 14, flex: 1, textAlign: "center",
        background: `${color}10`, border: `1px solid ${color}30`,
      }}>
        <div style={{fontSize: 24, fontWeight: 800, color, fontFamily: FONT_STACK}}>
          {ok ? "✓" : "✗"}
        </div>
        <div style={{fontSize: 20, fontWeight: 600, color: MUTED, fontFamily: FONT_STACK, marginTop: 4}}>
          {label}
        </div>
      </div>
    );
  };

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column"}}>
      <Audio src={staticFile(SCENES[2].file)} volume={0.9} />
      <Sequence from={30} durationInFrames={10}>
        <Audio src={staticFile("sfx/whoosh/whoosh-01.mp3")} volume={0.4} />
      </Sequence>
      <Sequence from={100} durationInFrames={10}>
        <Audio src={staticFile("sfx/pop/pop-01.mp3")} volume={0.3} />
      </Sequence>
      <Sequence from={200} durationInFrames={10}>
        <Audio src={staticFile("sfx/pop/pop-02.mp3")} volume={0.3} />
      </Sequence>
      <Sequence from={PHASE_B} durationInFrames={10}>
        <Audio src={staticFile("sfx/notification/ping-01.mp3")} volume={0.4} />
      </Sequence>
      <Sequence from={PHASE_B + 60} durationInFrames={10}>
        <Audio src={staticFile("sfx/notification/ping-02.mp3")} volume={0.35} />
      </Sequence>
      <Sequence from={PHASE_C} durationInFrames={60}>
        <Audio src={staticFile("sfx/riser/riser-02.mp3")} volume={0.3} />
      </Sequence>
      <Sequence from={PHASE_C + 80} durationInFrames={10}>
        <Audio src={staticFile("sfx/impact/impact-01.mp3")} volume={0.5} />
      </Sequence>

      {/* Card container — persists across phases */}
      <div style={{
        opacity: cardP,
        width: "92%", minHeight: 700, borderRadius: 24,
        background: "rgba(22,23,28,0.92)", border: "1px solid rgba(30,58,97,0.25)",
        padding: 28, display: "flex", flexDirection: "column", gap: 20,
        position: "relative", overflow: "hidden",
      }}>
        {/* PHASE A — Cashflow Timeline */}
        {showPhaseA && (
          <div style={{opacity: fadeA, display: "flex", flexDirection: "column", gap: 20}}>
            <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 700, color: MUTED, fontFamily: FONT_STACK, letterSpacing: 2}}>
              TIMELINE DO SEU CAIXA
            </div>
            <div style={{display: "flex", gap: 16, justifyContent: "center", alignItems: "flex-end", height: 300}}>
              <CashBar month="MES 1" amount="-R$5K" maxH={100} delay={40} />
              <CashBar month="MES 2" amount="-R$10K" maxH={200} delay={100} />
              <CashBar month="MES 3" amount="-R$15K" maxH={280} delay={180} />
            </div>
            <div style={{textAlign: "center"}}>
              <Counter value={15000} prefix="-R$" duration={45} fontSize={64} color={RED} delay={220} />
              <div style={{fontSize: 22, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK, marginTop: 8}}>
                ANTES DO 1o REAL VOLTAR
              </div>
            </div>
            <Pill text="90 DIAS" color={YELLOW} delay={250} icon="⏱" glow />
          </div>
        )}

        {/* PHASE B — A Ilusão */}
        {showPhaseB && (
          <div style={{
            opacity: fadeB, position: showPhaseA && fadeA > 0 ? "absolute" : "relative",
            inset: showPhaseA && fadeA > 0 ? 28 : undefined,
            display: "flex", flexDirection: "column", gap: 20,
          }}>
            <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 700, color: MUTED, fontFamily: FONT_STACK, letterSpacing: 2}}>
              O QUE O DONO VE
            </div>
            <div style={{display: "flex", gap: 12}}>
              <StatusMini label="CAC" ok delay={10} />
              <StatusMini label="LTV/CAC" ok delay={18} />
              <StatusMini label="TEMPO" ok={false} delay={26} />
            </div>

            {/* Cashflow vs Result bar */}
            <div style={{marginTop: 16}}>
              <div style={{display: "flex", justifyContent: "space-between", marginBottom: 8}}>
                <span style={{fontSize: 22, fontWeight: 700, color: RED, fontFamily: FONT_STACK}}>CAIXA</span>
                <span style={{fontSize: 22, fontWeight: 700, color: GREEN, fontFamily: FONT_STACK}}>RESULTADO</span>
              </div>
              <div style={{height: 20, borderRadius: 10, background: "rgba(255,255,255,0.06)", position: "relative", overflow: "hidden"}}>
                {/* Cash draining */}
                <div style={{
                  position: "absolute", left: 0, top: 0, bottom: 0,
                  width: `${interpolate(frame - PHASE_B, [0, 300], [70, 25], CLAMP)}%`,
                  background: `linear-gradient(90deg, ${RED}88, ${RED}44)`, borderRadius: 10,
                }} />
                {/* Result stuck far right */}
                <div style={{
                  position: "absolute", right: 0, top: 0, bottom: 0,
                  width: "15%", background: `${GREEN}44`, borderRadius: 10,
                }} />
              </div>
              <div style={{fontSize: 20, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK, textAlign: "center", marginTop: 12}}>
                O gap entre os dois: tempo que o caixa nao aguenta
              </div>
            </div>

            <div style={{textAlign: "center", marginTop: 12}}>
              <div style={{fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>
                MAS O DINHEIRO
              </div>
              <div style={{
                fontSize: FONT_SCALE.h2.min, fontWeight: 800, color: RED, fontFamily: FONT_STACK,
                textShadow: `0 0 30px ${RED}44`,
              }}>
                NAO VOLTA A TEMPO
              </div>
            </div>
          </div>
        )}

        {/* PHASE C — A Metáfora */}
        {showPhaseC && (
          <div style={{
            opacity: fadeC,
            position: showPhaseB && fadeB > 0 ? "absolute" : "relative",
            inset: showPhaseB && fadeB > 0 ? 28 : undefined,
            display: "flex", flexDirection: "column", gap: 24, alignItems: "center",
          }}>
            {/* Seed → Harvest timeline */}
            <div style={{display: "flex", alignItems: "center", gap: 16, width: "100%", justifyContent: "center"}}>
              <div style={{fontSize: 40}}>🌱</div>
              <div style={{flex: 1, height: 3, position: "relative"}}>
                <div style={{
                  position: "absolute", inset: 0,
                  background: `repeating-linear-gradient(90deg, ${MUTED}44 0, ${MUTED}44 8px, transparent 8px, transparent 16px)`,
                }} />
                {/* Traveling dot */}
                <div style={{
                  position: "absolute", top: -6, width: 14, height: 14, borderRadius: "50%",
                  background: YELLOW, boxShadow: `0 0 12px ${YELLOW}66`,
                  left: `${interpolate(frame - PHASE_C, [0, 200], [0, 85], CLAMP)}%`,
                }} />
              </div>
              <div style={{fontSize: 40}}>🌳</div>
            </div>
            <Pill text="3 MESES" color={RED} delay={PHASE_C + 20} icon="⏱" glow />

            {/* Alert card */}
            <div style={{
              padding: "20px 28px", borderRadius: 16, width: "100%",
              background: `${RED}08`, border: `1px solid ${RED}33`,
              display: "flex", alignItems: "center", gap: 16,
            }}>
              <span style={{fontSize: 36}}>⚠️</span>
              <div>
                <div style={{fontSize: 28, fontWeight: 700, color: RED, fontFamily: FONT_STACK, position: "relative"}}>
                  CAMPANHA CANCELADA
                  {/* Strike-through */}
                  <div style={{
                    position: "absolute", left: 0, top: "50%",
                    width: `${interpolate(frame - PHASE_C, [60, 90], [0, 100], CLAMP)}%`,
                    height: 3, background: YELLOW, borderRadius: 2,
                  }} />
                </div>
                <div style={{fontSize: 22, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK}}>
                  ...achando que nao funciona
                </div>
              </div>
            </div>

            {/* Punchline */}
            <div style={{textAlign: "center", marginTop: 8}}>
              <div style={{
                fontSize: FONT_SCALE.h2.min, fontWeight: 800, color: GREEN, fontFamily: FONT_STACK,
                textShadow: `0 0 30px ${GREEN}33`,
                transform: `scale(${spring({frame: frame - PHASE_C - 80, fps, config: SPRINGS.bouncy}) > 0
                  ? interpolate(spring({frame: frame - PHASE_C - 80, fps, config: SPRINGS.bouncy}), [0, 1], [0.8, 1], CLAMP)
                  : 0})`,
                opacity: spring({frame: frame - PHASE_C - 80, fps, config: SPRINGS.bouncy}),
              }}>
                MAS FUNCIONAVA.
              </div>
              <div style={{
                fontSize: FONT_SCALE.body.min - 8, fontWeight: 600, color: MUTED, fontFamily: FONT_STACK, marginTop: 8,
                opacity: spring({frame: frame - PHASE_C - 100, fps, config: SPRINGS.smooth}),
              }}>
                So que o caixa nao esperou.
              </div>
            </div>
          </div>
        )}
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 4: COMPROMISSO + DESAFIO ─────────────────────────────────
const S4: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const headerP = spring({frame: frame - 5, fps, config: SPRINGS.heavy});
  const avgP = spring({frame: frame - 180, fps, config: SPRINGS.smooth});
  const alertP = spring({frame: frame - 260, fps, config: SPRINGS.bouncy});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 16}}>
      <Audio src={staticFile(SCENES[3].file)} volume={0.9} />
      <Sequence from={30} durationInFrames={10}>
        <Audio src={staticFile("sfx/pop/pop-01.mp3")} volume={0.3} />
      </Sequence>
      <Sequence from={60} durationInFrames={10}>
        <Audio src={staticFile("sfx/pop/pop-02.mp3")} volume={0.3} />
      </Sequence>
      <Sequence from={90} durationInFrames={10}>
        <Audio src={staticFile("sfx/pop/pop-03.mp3")} volume={0.3} />
      </Sequence>
      <Sequence from={120} durationInFrames={10}>
        <Audio src={staticFile("sfx/notification/ping-01.mp3")} volume={0.35} />
      </Sequence>
      <Sequence from={150} durationInFrames={10}>
        <Audio src={staticFile("sfx/notification/ping-02.mp3")} volume={0.35} />
      </Sequence>
      <Sequence from={200} durationInFrames={10}>
        <Audio src={staticFile("sfx/impact/impact-01.mp3")} volume={0.45} />
      </Sequence>

      {/* Header */}
      <div style={{
        opacity: headerP, display: "flex", alignItems: "center", gap: 12,
        transform: `translateY(${interpolate(headerP, [0, 1], [20, 0], CLAMP)}px)`,
      }}>
        <span style={{fontSize: 32}}>📋</span>
        <span style={{fontSize: FONT_SCALE.label.min, fontWeight: 700, color: YELLOW, fontFamily: FONT_STACK, letterSpacing: 2}}>
          DESAFIO DE HOJE
        </span>
      </div>

      {/* Client rows */}
      <div style={{width: "92%", display: "flex", flexDirection: "column", gap: 8}}>
        <ClientRow num={1} contact="10/jan" payment="15/mar" days={64} delay={30} />
        <ClientRow num={2} contact="22/jan" payment="05/mar" days={42} delay={60} />
        <ClientRow num={3} contact="03/fev" payment="28/abr" days={84} delay={90} />
        <ClientRow num={4} contact="15/fev" payment="20/mar" days={33} delay={120} />
        <ClientRow num={5} contact="01/mar" payment="12/mai" days={72} delay={150} />
      </div>

      {/* Average */}
      <div style={{
        opacity: avgP, textAlign: "center", marginTop: 8,
        transform: `scale(${interpolate(avgP, [0, 1], [0.9, 1], CLAMP)})`,
      }}>
        <div style={{fontSize: 24, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK}}>MEDIA</div>
        <Counter value={59} suffix=" DIAS" duration={30} fontSize={56} color={YELLOW} delay={190} fontWeight={900} />
      </div>

      {/* Alert */}
      <div style={{
        opacity: alertP,
        transform: `scale(${interpolate(alertP, [0, 1], [0.8, 1], CLAMP)})`,
        padding: "16px 28px", borderRadius: 16,
        background: `${RED}10`, border: `1px solid ${RED}30`,
        textAlign: "center",
      }}>
        <div style={{fontSize: FONT_SCALE.body.min - 8, fontWeight: 700, color: RED, fontFamily: FONT_STACK}}>
          {">"}60 dias sem reserva?
        </div>
        <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 800, color: WHITE, fontFamily: FONT_STACK, marginTop: 4}}>
          ESSE E O PROBLEMA.
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 5: FECHAMENTO TRILOGIA ───────────────────────────────────
const S5: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const titleP = spring({frame: frame - 5, fps, config: SPRINGS.heavy});
  const ctaP = spring({frame: frame - 120, fps, config: SPRINGS.bouncy});
  const logoP = spring({frame: frame - 200, fps, config: SPRINGS.smooth});
  const pulse = frame > 140 ? 1 + Math.sin((frame - 140) * 0.06) * 0.02 : 1;

  return (
    <AbsoluteFill style={{
      justifyContent: "center", alignItems: "center",
      padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`,
      flexDirection: "column", gap: 24,
      background: `linear-gradient(170deg, ${IDV.bg} 0%, ${BLUE} 100%)`,
    }}>
      <Audio src={staticFile(SCENES[4].file)} volume={0.9} />
      <Sequence from={5} durationInFrames={10}>
        <Audio src={staticFile("sfx/impact/impact-02.mp3")} volume={0.5} />
      </Sequence>
      <Sequence from={40} durationInFrames={10}>
        <Audio src={staticFile("sfx/pop/pop-01.mp3")} volume={0.3} />
      </Sequence>
      <Sequence from={50} durationInFrames={10}>
        <Audio src={staticFile("sfx/pop/pop-02.mp3")} volume={0.3} />
      </Sequence>
      <Sequence from={60} durationInFrames={10}>
        <Audio src={staticFile("sfx/pop/pop-03.mp3")} volume={0.3} />
      </Sequence>
      <Sequence from={120} durationInFrames={10}>
        <Audio src={staticFile("sfx/success/success-01.mp3")} volume={0.4} />
      </Sequence>

      {/* Title */}
      <div style={{
        opacity: titleP, textAlign: "center",
        transform: `translateY(${interpolate(titleP, [0, 1], [30, 0], CLAMP)}px)`,
      }}>
        <GradientText text="3 NUMEROS" colors={[WHITE, "#ccc"]} fontSize={FONT_SCALE.h1.min} delay={5} springProfile="heavy" />
        <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK, marginTop: 8}}>
          que separam crescimento de incendio
        </div>
      </div>

      {/* Trilogy badges */}
      <div style={{display: "flex", gap: 16}}>
        <TrilogyBadge num="①" label="CAC" sub="Quanto custa trazer" color={GREEN} delay={35} />
        <TrilogyBadge num="②" label="LTV/CAC" sub="Caro ou barato" color={GREEN} delay={45} />
        <TrilogyBadge num="③" label="TEMPO" sub="Quanto demora pra voltar" color={YELLOW} delay={55} highlight />
      </div>

      {/* CTA */}
      <div style={{
        opacity: ctaP, textAlign: "center",
        transform: `scale(${interpolate(ctaP, [0, 1], [0.85, 1], CLAMP) * pulse})`,
      }}>
        <div style={{fontSize: FONT_SCALE.h2.min, fontWeight: 800, color: WHITE, fontFamily: FONT_STACK}}>
          SALVA ESSA TRILOGIA.
        </div>
        <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 700, color: GREEN, fontFamily: FONT_STACK, marginTop: 4}}>
          MANDA PRO TEU SOCIO.
        </div>
      </div>

      {/* Logo + byline */}
      <div style={{
        opacity: logoP, display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
        transform: `translateY(${interpolate(logoP, [0, 1], [15, 0], CLAMP)}px)`,
      }}>
        <Img src={staticFile("assets/profiles/logo-cafe-roi-dark-bg.png")} style={{height: 80, objectFit: "contain"}} />
        <div style={{fontSize: 24, fontWeight: 400, color: `${MUTED}88`, fontFamily: FONT_STACK}}>
          Ate amanha.
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ─── MAIN COMPOSITION ────────────────────────────────────────────────
export const CafeEp10: React.FC = () => {
  let offset = 0;
  return (
    <AbsoluteFill style={{backgroundColor: "#000"}}>
      <div style={{position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 35%, #0a1628, #000 70%)`}} />
      <Noise opacity={0.02} />
      <FilmGrain opacity={0.04} />
      <Vignette intensity={0.7} />

      {SCENES.map(({dur, dir}, i) => {
        const from = offset;
        offset += dur;
        const Comp = [S1, S2, S3, S4, S5][i];
        return (
          <Sequence key={i} from={from} durationInFrames={dur}>
            <SceneFade duration={dur}>
              <SceneTransition type="in" direction={dir} springProfile="smooth">
                <Comp />
              </SceneTransition>
            </SceneFade>
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};

const SceneFade: React.FC<{duration: number; children: React.ReactNode}> = ({duration, children}) => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 8], [0, 1], CLAMP);
  const fadeOut = interpolate(frame, [duration - 10, duration], [1, 0], CLAMP);
  return <AbsoluteFill style={{opacity: Math.min(fadeIn, fadeOut)}}>{children}</AbsoluteFill>;
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
